import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, RefreshCw, GripVertical, Plus, Users, CalendarDays,
  Trophy, Swords, TrendingUp, TrendingDown, Minus, BarChart3, Search, X, Mail, Download
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SESSION_COLORS = {
  Full: "bg-emerald-100 text-emerald-800 border-emerald-300",
  "6Wk": "bg-blue-100 text-blue-800 border-blue-300",
  HS1: "bg-amber-100 text-amber-800 border-amber-300",
  HS2: "bg-orange-100 text-orange-800 border-orange-300",
  F20: "bg-purple-100 text-purple-800 border-purple-300",
  F25: "bg-pink-100 text-pink-800 border-pink-300",
  F30: "bg-rose-100 text-rose-800 border-rose-300",
};

const NAV_TEAM_COLORS = {
  "Chaos Crew": "#39FF14", "The Dreamers": "#8B5CF6", "Focus Force": "#2563EB",
  "The Fearless": "#DC2626", "Mind Masters": "#18181B", "The Hyped": "#EA580C",
  "The Chill Zone": "#38BDF8", "The Overthinkers": "#6B7280",
  "The Positive Vibes": "#EAB308", "The Dark Side": "#581C87",
};

const NAV_TEAM_VIBES = {
  "Chaos Crew": "Wild, unpredictable, loud energy",
  "The Dreamers": "Creative, imaginative, artsy vibe",
  "Focus Force": "Locked in, competitive, disciplined",
  "The Fearless": "Bold, aggressive, never back down",
  "Mind Masters": "Strategic, calculated, chess not checkers",
  "The Hyped": "Energy, hype squad, loudest team",
  "The Chill Zone": "Relaxed but sneaky competitive",
  "The Overthinkers": "Funny, dramatic, overanalyzing everything",
  "The Positive Vibes": "Optimistic, team-first, morale builders",
  "The Dark Side": "Mysterious, intense, slightly villain energy",
};

// Activity → Location mapping
const ACTIVITY_LOCATIONS = {
  "Hockey": "Hockey Rink",
  "Softball": "Softball 1",
  "Spot Shot": "Basketball",
  "Euro Handball": "Field 2",
  "Volleyball": "Volleyball",
};

function NavHeader({ title, subtitle, onBack, children }) {
  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="px-2"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-sm font-bold">{title}</h1>
          {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </header>
  );
}

// ═══════════════════════════════════════
// LANDING — 5 Cards
// ═══════════════════════════════════════

export function NavigatorIntramuralsLanding({ headers, onBack, isEditable }) {
  const [subView, setSubView] = useState(null);

  if (subView === "teams") return <NavTeamSorterView headers={headers} onBack={() => setSubView(null)} isEditable={isEditable} />;
  if (subView === "schedule") return <NavScheduleView headers={headers} onBack={() => setSubView(null)} isEditable={isEditable} />;
  if (subView === "standings") return <NavStandingsView headers={headers} onBack={() => setSubView(null)} />;
  if (subView === "rankings") return <NavRankingsView headers={headers} onBack={() => setSubView(null)} />;
  if (subView === "playoffs") return <NavPlayoffsView headers={headers} onBack={() => setSubView(null)} isEditable={isEditable} />;
  if (subView === "championship") return <NavChampionshipView headers={headers} onBack={() => setSubView(null)} isEditable={isEditable} />;

  const cards = [
    { id: "teams", label: "Navigator Intramural Teams", icon: Users, color: "teal", desc: "10 co-ed teams" },
    { id: "schedule", label: "Schedule", icon: CalendarDays, color: "emerald", desc: "6-week round robin" },
    { id: "standings", label: "Standings", icon: BarChart3, color: "blue", desc: "W-L-T cumulative" },
    { id: "rankings", label: "Rankings", icon: Trophy, color: "amber", desc: "Power rankings" },
    { id: "playoffs", label: "Playoffs", icon: Swords, color: "rose", desc: "Bracket builder" },
    { id: "championship", label: "Championship", icon: Trophy, color: "violet", desc: "Final bracket" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="px-2"><ArrowLeft className="w-4 h-4" /></Button>
          <h2 className="font-heading font-bold text-lg">Navigator Intramurals</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map(card => (
          <button key={card.id} onClick={() => setSubView(card.id)}
            className={`flex items-center gap-4 p-5 rounded-2xl border-2 bg-${card.color}-50 border-${card.color}-200 hover:border-${card.color}-400 hover:shadow-lg transition-all active:scale-[0.98] text-left`}
            data-testid={`nav-intramurals-${card.id}`}
          >
            <div className={`w-12 h-12 rounded-full bg-${card.color}-100 flex items-center justify-center border border-${card.color}-200`}>
              <card.icon className={`w-6 h-6 text-${card.color}-600`} />
            </div>
            <div>
              <span className={`text-sm font-bold text-${card.color}-800`}>{card.label}</span>
              <p className={`text-[10px] text-${card.color}-500`}>{card.desc}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// TEAM SORTER
// ═══════════════════════════════════════

function NavTeamSorterView({ headers, onBack, isEditable }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState(false);
  const [tab, setTab] = useState("teams");
  const [dragOverTeam, setDragOverTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [counselorsByTeam, setCounselorsByTeam] = useState({});

  const fetchTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nav-intramurals/teams`, { headers });
      setData(res.data);
    } catch { toast.error("Failed to load teams"); }
    setLoading(false);
  }, []);

  const fetchCounselors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nav-intramurals/counselors`, { headers });
      setCounselorsByTeam(res.data?.by_team || {});
    } catch { /* silent — counselors are optional */ }
  }, []);

  useEffect(() => { fetchTeams(); fetchCounselors(); }, [fetchTeams, fetchCounselors]);

  const handleSort = async () => {
    if (!window.confirm("This will re-sort all campers and override manual changes. Are you sure?")) return;
    setSorting(true);
    try {
      const res = await axios.post(`${API}/nav-intramurals/sort`, {}, { headers });
      setData(res.data);
      toast.success("Teams sorted!");
    } catch (err) { toast.error(err.response?.data?.detail || "Sort failed"); }
    setSorting(false);
  };

  const handleDrop = async (toTeam, e) => {
    e.preventDefault();
    setDragOverTeam(null);
    const camperId = e.dataTransfer.getData("camper_id");
    const fromTeam = e.dataTransfer.getData("from_team");
    if (!camperId || !fromTeam || fromTeam === toTeam) return;
    try {
      await axios.put(`${API}/nav-intramurals/move`, {
        camper_id: camperId, from_team: fromTeam, to_team: toTeam
      }, { headers });
      fetchTeams();
    } catch { toast.error("Move failed"); }
  };

  const teams = data?.teams || {};
  const teamNames = Object.keys(teams);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const allCampers = [];
  for (const [team, members] of Object.entries(teams)) {
    for (const c of members) { allCampers.push({ ...c, team }); }
  }

  const filteredRoster = searchTerm
    ? allCampers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.group_code.toLowerCase().includes(searchTerm.toLowerCase()) || c.team.toLowerCase().includes(searchTerm.toLowerCase()))
    : allCampers;

  return (
    <div className="min-h-screen bg-background">
      <NavHeader title="Navigator Intramural Teams" subtitle={data?.sorted ? `${teamNames.length} co-ed teams sorted` : "Not sorted yet"} onBack={onBack}>
        {isEditable && (
          <Button size="sm" className="h-8 text-xs" onClick={handleSort} disabled={sorting} data-testid="nav-sort-teams-btn">
            {sorting ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            {data?.sorted ? "Re-Sort" : "Sort Teams"}
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="nav-intramural-pdf-btn"
          onClick={async () => {
            try {
              const res = await fetch(`${API}/intramurals/navigators/export/pdf`, { headers });
              if (!res.ok) throw new Error("download failed");
              const blob = await res.blob();
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "navigator_intramurals.pdf";
              a.click();
            } catch { toast.error("PDF export failed"); }
          }}>
          <Download className="w-3 h-3 mr-1" /> PDF
        </Button>
      </NavHeader>

      <div className="border-b px-4">
        <div className="flex gap-4">
          <button onClick={() => setTab("teams")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "teams" ? "border-teal-600 text-teal-700" : "border-transparent text-muted-foreground"}`} data-testid="nav-tab-teams">Team View</button>
          <button onClick={() => setTab("roster")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "roster" ? "border-teal-600 text-teal-700" : "border-transparent text-muted-foreground"}`} data-testid="nav-tab-roster">Roster View</button>
        </div>
      </div>

      {tab === "teams" && (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {teamNames.map(teamName => {
            const members = teams[teamName] || [];
            const color = NAV_TEAM_COLORS[teamName] || "#666";
            const vibe = NAV_TEAM_VIBES[teamName] || "";
            const byGroup = {};
            members.forEach(c => { byGroup[c.group_code] = byGroup[c.group_code] || []; byGroup[c.group_code].push(c); });
            const sessionCounts = {};
            members.forEach(c => { sessionCounts[c.session_label] = (sessionCounts[c.session_label] || 0) + 1; });
            const boysCount = members.filter(c => c.group_code.startsWith("B")).length;
            const girlsCount = members.filter(c => c.group_code.startsWith("G")).length;

            return (
              <Card key={teamName}
                className={`overflow-hidden transition-all ${dragOverTeam === teamName ? "ring-2 ring-teal-500 bg-teal-50/30" : ""}`}
                onDragOver={isEditable ? (e) => { e.preventDefault(); setDragOverTeam(teamName); } : undefined}
                onDragLeave={isEditable ? () => setDragOverTeam(null) : undefined}
                onDrop={isEditable ? (e) => handleDrop(teamName, e) : undefined}
                data-testid={`nav-team-card-${teamName.replace(/[^a-zA-Z]/g, "")}`}
              >
                <div className="p-2 text-center text-white text-xs font-bold" style={{ backgroundColor: color }}>{teamName}</div>
                <div className="px-2 py-1 text-center"><p className="text-[9px] text-muted-foreground italic">{vibe}</p></div>
                <CardContent className="p-2 space-y-1">
                  <div className="text-[10px] text-muted-foreground border-b pb-1 mb-1" data-testid={`nav-team-counselors-${teamName.replace(/[^a-zA-Z]/g, "")}`}>
                    {(counselorsByTeam[teamName] || []).length === 0 ? (
                      <div className="italic text-slate-400">No counselors uploaded</div>
                    ) : (
                      (counselorsByTeam[teamName] || []).map(c => (
                        <div key={c.id} className="font-semibold text-slate-700">{c.full_name || `${c.first_name} ${c.last_name}`.trim()}</div>
                      ))
                    )}
                  </div>
                  {Object.keys(byGroup).sort().map(gc => (
                    <div key={gc}>
                      <div className="text-[10px] font-bold text-muted-foreground mt-1">{gc}</div>
                      {byGroup[gc].map(c => (
                        <div key={c.id} draggable={isEditable}
                          onDragStart={isEditable ? (e) => { e.dataTransfer.setData("camper_id", c.id); e.dataTransfer.setData("from_team", teamName); } : undefined}
                          className={`flex items-center justify-between py-1 px-1.5 rounded text-[11px] hover:bg-muted/50 border border-transparent hover:border-muted ${isEditable ? "cursor-grab active:cursor-grabbing" : ""} ${c.manual_move ? "bg-yellow-50 border-yellow-200" : ""}`}
                          data-testid={`nav-camper-${c.id}`}
                        >
                          <span className="truncate">{isEditable && <GripVertical className="w-2.5 h-2.5 inline mr-0.5 text-muted-foreground" />}{c.name}</span>
                          <Badge variant="outline" className={`text-[8px] px-1 py-0 ml-1 ${SESSION_COLORS[c.session_label] || ""}`}>{c.session_label}</Badge>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="border-t pt-1 mt-1 text-[10px] text-muted-foreground text-center space-y-0.5">
                    <div className="font-semibold">{members.length} campers</div>
                    <div>Boys: {boysCount} | Girls: {girlsCount}</div>
                    <div>{Object.entries(sessionCounts).map(([k, v]) => `${k}: ${v}`).join(" | ")}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {tab === "roster" && (
        <div className="p-4 max-w-5xl mx-auto">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by name, group, or team..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="nav-roster-search" />
          </div>
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left">
              <th className="py-2 px-2 font-semibold">Name</th><th className="py-2 px-2 font-semibold">Group</th>
              <th className="py-2 px-2 font-semibold">Gender</th><th className="py-2 px-2 font-semibold">Session</th>
              <th className="py-2 px-2 font-semibold">Team</th>
            </tr></thead>
            <tbody>
              {filteredRoster.sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                <tr key={c.id} className="border-b hover:bg-muted/30">
                  <td className="py-1.5 px-2">{c.name}</td><td className="py-1.5 px-2">{c.group_code}</td>
                  <td className="py-1.5 px-2">{c.group_code.startsWith("B") ? "Boy" : "Girl"}</td>
                  <td className="py-1.5 px-2"><Badge variant="outline" className={`text-[9px] ${SESSION_COLORS[c.session_label] || ""}`}>{c.session_label}</Badge></td>
                  <td className="py-1.5 px-2"><span className="font-medium" style={{ color: NAV_TEAM_COLORS[c.team] }}>{c.team}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-muted-foreground mt-2">{filteredRoster.length} campers</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// SCHEDULE (SCORECARD)
// ═══════════════════════════════════════

function NavScheduleView({ headers, onBack, isEditable }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/nav-intramurals/schedule`, { headers });
      setData(res.data);
    } catch { toast.error("Failed to load schedule"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const handleScoreChange = (key, field, value) => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        schedule: prev.schedule.map(week => ({
          ...week,
          monday_games: (week.monday_games || []).map(g => g.key === key ? { ...g, [field]: value } : g),
          friday_games: (week.friday_games || []).map(g => g.key === key ? { ...g, [field]: value } : g),
          games: (week.games || []).map(g => g.key === key ? { ...g, [field]: value } : g),
        }))
      };
    });
  };

  const handleScoreBlur = async (key) => {
    const game = data?.schedule?.flatMap(w => [...(w.monday_games || []), ...(w.friday_games || [])]).find(g => g.key === key);
    if (!game) return;
    try {
      await axios.put(`${API}/nav-intramurals/score`, { key, score1: game.score1, score2: game.score2 }, { headers });
    } catch {
      toast.error("Failed to save score");
      fetchSchedule();
    }
  };

  const handleGenerateDay2 = async () => {
    if (data?.day2_enabled) {
      if (!window.confirm("A Friday schedule already exists. Regenerate? This will overwrite the current Friday matchups (Friday scores will be reset).")) return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/nav-intramurals/schedule/day2/generate`, {}, { headers });
      const diag = res.data?.diagnostics || {};
      toast.success(`Friday schedule generated! ${diag.teams_missing_a_sport === 0 ? "All teams play every sport ≥1x." : `${diag.teams_missing_a_sport} team-sport gaps`} · ${diag.rematches} rematches.`);
      fetchSchedule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate Friday schedule");
    }
    setGenerating(false);
  };

  const handleClearDay2 = async () => {
    if (!window.confirm("Clear the Friday schedule? Friday scores will be lost.")) return;
    try {
      await axios.delete(`${API}/nav-intramurals/schedule/day2`, { headers });
      toast.success("Friday schedule cleared");
      fetchSchedule();
    } catch { toast.error("Failed to clear Friday schedule"); }
  };

  const handleRebalance = async () => {
    if (!window.confirm("Rebalance the schedule? Weeks with scores already entered stay exactly as played. All remaining weeks (Monday AND Friday) get regenerated so no matchup repeats and activities stay balanced.")) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/nav-intramurals/schedule/rebalance`, {}, { headers });
      const d = res.data || {};
      toast.success(`Rebalanced! Kept weeks ${(d.locked_weeks || []).join(", ") || "none"} · regenerated weeks ${(d.regenerated_weeks || []).join(", ")} · ${d.pairs_played_3plus === 0 ? "no matchup happens 3+ times" : `${d.pairs_played_3plus} matchups still 3+ (unavoidable)`}`);
      fetchSchedule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rebalance schedule");
    }
    setGenerating(false);
  };

  const renderGameRow = (game) => (
    <div key={game.key} className="flex flex-wrap items-center gap-2 text-xs p-2 rounded-lg bg-muted/20">
      <span className="font-semibold text-right min-w-[110px]" style={{ color: NAV_TEAM_COLORS[game.team1] }}>{game.team1}</span>
      <span className="text-muted-foreground text-[10px]">vs.</span>
      <span className="font-semibold min-w-[110px]" style={{ color: NAV_TEAM_COLORS[game.team2] }}>{game.team2}</span>
      <span className="text-muted-foreground text-[10px] px-2 py-0.5 bg-white rounded border">{game.activity}</span>
      <span className="text-muted-foreground text-[10px]">{game.location}</span>
      <div className="flex items-center gap-1 ml-auto">
        {isEditable ? (
          <>
            <span className="text-[9px] text-muted-foreground hidden sm:inline">{game.team1?.split(" ")[0]}:</span>
            <input
              type="number"
              className="w-14 h-8 text-xs text-center border rounded-md bg-white focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none"
              value={game.score1 ?? ""}
              onChange={e => handleScoreChange(game.key, "score1", e.target.value)}
              onBlur={() => handleScoreBlur(game.key)}
              placeholder="—"
              data-testid={`nav-score-${game.key}-1`}
            />
            <span className="text-[9px] text-muted-foreground hidden sm:inline">{game.team2?.split(" ")[0]}:</span>
            <input
              type="number"
              className="w-14 h-8 text-xs text-center border rounded-md bg-white focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none"
              value={game.score2 ?? ""}
              onChange={e => handleScoreChange(game.key, "score2", e.target.value)}
              onBlur={() => handleScoreBlur(game.key)}
              placeholder="—"
              data-testid={`nav-score-${game.key}-2`}
            />
          </>
        ) : (
          <>
            <span className="w-14 h-8 text-xs text-center font-bold inline-flex items-center justify-center bg-white border rounded-md">{game.score1 ?? "—"}</span>
            <span className="w-14 h-8 text-xs text-center font-bold inline-flex items-center justify-center bg-white border rounded-md">{game.score2 ?? "—"}</span>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <NavHeader title="Navigator Schedule" subtitle={data?.day2_enabled ? "6 Weeks · Mondays + Fridays" : "6 Weeks · Mondays only"} onBack={onBack}>
        {isEditable && (
          <>
            <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" onClick={handleRebalance} disabled={generating} data-testid="nav-rebalance-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Working..." : "Rebalance Remaining Weeks"}
            </Button>
            {data?.day2_enabled ? (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleGenerateDay2} disabled={generating} data-testid="nav-regen-friday-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${generating ? "animate-spin" : ""}`} />
                {generating ? "Generating..." : "Regenerate Friday"}
              </Button>
            ) : (
              <Button size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700" onClick={handleGenerateDay2} disabled={generating} data-testid="nav-generate-friday-btn">
                <Plus className={`w-3 h-3 mr-1 ${generating ? "animate-spin" : ""}`} />
                {generating ? "Generating..." : "Generate Friday Schedule"}
              </Button>
            )}
            {data?.day2_enabled && (
              <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600 hover:text-red-700" onClick={handleClearDay2} data-testid="nav-clear-friday-btn">
                <X className="w-3 h-3 mr-1" /> Clear Friday
              </Button>
            )}
          </>
        )}
      </NavHeader>

      {loading ? <div className="text-center py-10 text-muted-foreground">Loading...</div> : data && (
        <div className="p-4 max-w-5xl mx-auto space-y-6">
          {(data.schedule || []).map(week => (
            <Card key={week.week}>
              <CardContent className="pt-4">
                <h3 className="text-sm font-bold mb-3">Week {week.week}</h3>

                <div className="mb-2 flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-100 px-2 py-0.5 rounded">Monday</span>
                  <span className="text-[10px] text-muted-foreground">Day 1</span>
                </div>
                <div className="space-y-2 mb-4">
                  {(week.monday_games || week.games || []).map(renderGameRow)}
                </div>

                {(week.friday_games || []).length > 0 && (
                  <>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 bg-violet-100 px-2 py-0.5 rounded">Friday</span>
                      <span className="text-[10px] text-muted-foreground">Day 2</span>
                    </div>
                    <div className="space-y-2">
                      {week.friday_games.map(renderGameRow)}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// STANDINGS
// ═══════════════════════════════════════

export function NavStandingsView({ headers, onBack }) {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/nav-intramurals/standings`, { headers });
        setStandings(res.data.standings || []);
      } catch { toast.error("Failed to load standings"); }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <NavHeader title="Navigator Standings" subtitle="Auto-calculated from scores" onBack={onBack} />
      {loading ? <div className="text-center py-10 text-muted-foreground">Loading...</div> : (
        <div className="p-4 max-w-4xl mx-auto">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="nav-standings-table">
              <thead><tr className="border-b bg-muted/30">
                <th className="py-2 px-2 text-left font-semibold w-10">Rank</th>
                <th className="py-2 px-2 text-left font-semibold">Team Name</th>
                <th className="py-2 px-2 text-center font-semibold">W</th><th className="py-2 px-2 text-center font-semibold">L</th>
                <th className="py-2 px-2 text-center font-semibold">T</th><th className="py-2 px-2 text-center font-semibold">PF</th>
                <th className="py-2 px-2 text-center font-semibold">PA</th><th className="py-2 px-2 text-center font-semibold">PD</th>
                <th className="py-2 px-2 text-center font-semibold">Total Pts</th>
              </tr></thead>
              <tbody>
                {standings.map(s => (
                  <tr key={s.team} className="border-b hover:bg-muted/20">
                    <td className="py-2 px-2 font-bold text-muted-foreground">#{s.rank}</td>
                    <td className="py-2 px-2"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} /><span className="font-semibold" style={{ color: s.color }}>{s.team}</span></div></td>
                    <td className="py-2 px-2 text-center font-bold text-green-600">{s.w}</td>
                    <td className="py-2 px-2 text-center font-bold text-red-500">{s.l}</td>
                    <td className="py-2 px-2 text-center font-bold text-gray-500">{s.t}</td>
                    <td className="py-2 px-2 text-center">{s.pf}</td><td className="py-2 px-2 text-center">{s.pa}</td>
                    <td className="py-2 px-2 text-center font-semibold" style={{ color: s.pd > 0 ? "#16a34a" : s.pd < 0 ? "#dc2626" : "#6b7280" }}>{s.pd > 0 ? `+${s.pd}` : s.pd}</td>
                    <td className="py-2 px-2 text-center font-bold">{s.total_pts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {standings.length === 0 && <p className="text-center py-8 text-muted-foreground text-sm">No scores entered yet.</p>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// RANKINGS
// ═══════════════════════════════════════

export function NavRankingsView({ headers, onBack }) {
  const [rankings, setRankings] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/nav-intramurals/rankings`, { headers });
        setRankings(res.data.rankings || []);
        setCurrentWeek(res.data.current_week || 0);
      } catch { toast.error("Failed to load rankings"); }
      setLoading(false);
    })();
  }, []);

  const TrendIcon = ({ trend }) => {
    if (trend === "up") return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === "down") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const MEDAL_BG = { 1: "bg-yellow-50 border-yellow-300", 2: "bg-gray-50 border-gray-300", 3: "bg-amber-50 border-amber-300" };

  return (
    <div className="min-h-screen bg-background">
      <NavHeader title="Navigator Rankings" subtitle={currentWeek > 0 ? `Through Week ${currentWeek}` : "No scores yet"} onBack={onBack} />
      {loading ? <div className="text-center py-10 text-muted-foreground">Loading...</div> : (
        <div className="p-4 max-w-2xl mx-auto space-y-2">
          {rankings.map(r => (
            <Card key={r.team} className={`overflow-hidden border ${MEDAL_BG[r.rank] || ""}`}>
              <div className="flex items-center p-3 gap-3">
                <div className="text-xl font-black w-9 text-center text-muted-foreground">#{r.rank}</div>
                <div className="w-4 h-10 rounded-sm flex-shrink-0" style={{ backgroundColor: r.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate" style={{ color: r.color }}>{r.team}</div>
                  <div className="text-[10px] text-muted-foreground">{r.w}-{r.l}-{r.t} &nbsp;|&nbsp; {r.total_pts} pts</div>
                </div>
                <TrendIcon trend={r.trend} />
              </div>
            </Card>
          ))}
          {rankings.length === 0 && <p className="text-center py-8 text-muted-foreground text-sm">No scores entered yet.</p>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// PLAYOFFS — Redesigned with Game Word Bank
// ═══════════════════════════════════════

export function NavPlayoffsView({ headers, onBack, isEditable }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rankings, setRankings] = useState([]);
  // For mobile tap-to-place
  const [selectedItem, setSelectedItem] = useState(null); // { type: "team"|"activity", value: "..." }

  const fetchData = useCallback(async () => {
    try {
      const [playoffRes, rankingsRes] = await Promise.all([
        axios.get(`${API}/nav-intramurals/playoffs`, { headers }),
        axios.get(`${API}/nav-intramurals/rankings`, { headers }),
      ]);
      setData(playoffRes.data);
      setRankings(rankingsRes.data.rankings || []);
    } catch { toast.error("Failed to load playoffs"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async (games) => {
    setSaving(true);
    try {
      await axios.put(`${API}/nav-intramurals/playoffs`, { games }, { headers });
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const addGame = () => {
    const games = [...(data?.games || []), { team1: "", team2: "", score1: "", score2: "", activity: "", location: "" }];
    setData(prev => ({ ...prev, games }));
    save(games);
  };

  const updateGame = (index, field, value) => {
    const games = [...(data?.games || [])];
    games[index] = { ...games[index], [field]: value };
    // Auto-populate location when activity is set
    if (field === "activity" && ACTIVITY_LOCATIONS[value]) {
      games[index].location = ACTIVITY_LOCATIONS[value];
    }
    setData(prev => ({ ...prev, games }));
  };

  const saveGames = () => save(data?.games || []);

  const removeGame = (index) => {
    const games = (data?.games || []).filter((_, i) => i !== index);
    setData(prev => ({ ...prev, games }));
    save(games);
  };

  // Handle tap-to-place for a team drop zone
  const handleDropZoneTap = (gameIdx, field) => {
    if (!isEditable || !selectedItem) return;
    if (selectedItem.type === "team" && (field === "team1" || field === "team2")) {
      updateGame(gameIdx, field, selectedItem.value);
      setSelectedItem(null);
      saveGames();
    }
    if (selectedItem.type === "activity" && field === "activity") {
      updateGame(gameIdx, "activity", selectedItem.value);
      if (ACTIVITY_LOCATIONS[selectedItem.value]) {
        const games = [...(data?.games || [])];
        games[gameIdx] = { ...games[gameIdx], activity: selectedItem.value, location: ACTIVITY_LOCATIONS[selectedItem.value] };
        setData(prev => ({ ...prev, games }));
        save(games);
      }
      setSelectedItem(null);
    }
  };

  // Handle desktop drag-and-drop for teams
  const handleTeamDrop = (gameIdx, field, e) => {
    e.preventDefault();
    const team = e.dataTransfer.getData("team");
    if (team && (field === "team1" || field === "team2")) {
      updateGame(gameIdx, field, team);
      saveGames();
    }
  };

  // Handle desktop drag-and-drop for activities
  const handleActivityDrop = (gameIdx, e) => {
    e.preventDefault();
    const activity = e.dataTransfer.getData("activity");
    if (activity) {
      const games = [...(data?.games || [])];
      games[gameIdx] = { ...games[gameIdx], activity, location: ACTIVITY_LOCATIONS[activity] || "" };
      setData(prev => ({ ...prev, games }));
      save(games);
    }
  };

  const teamBank = data?.team_bank || [];
  const activities = data?.activities || ACTIVITY_LOCATIONS;

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <NavHeader title="Navigator Playoffs" onBack={onBack}>
        {isEditable && (
          <Button size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700" onClick={addGame} data-testid="nav-add-game-btn">
            <Plus className="w-3 h-3 mr-1" /> New Game
          </Button>
        )}
      </NavHeader>

      <div className="p-4 max-w-5xl mx-auto space-y-4">

        {/* Selected item indicator (mobile) */}
        {selectedItem && (
          <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-300 rounded-lg animate-pulse">
            <span className="text-xs font-semibold text-teal-800">
              Selected: <span className="font-bold">{selectedItem.value}</span> — tap a {selectedItem.type === "team" ? "team slot" : "game slot"} to place it
            </span>
            <Button variant="ghost" size="sm" className="h-6 px-2 ml-auto" onClick={() => setSelectedItem(null)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}

        {isEditable && (
          <>
            {/* Team Bank */}
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground mb-2 font-semibold">TEAM BANK — drag or tap to select, then tap a team slot</p>
                <div className="flex flex-wrap gap-2">
                  {teamBank.map(t => {
                    const rankData = rankings.find(r => r.team === t);
                    const rank = rankData?.rank || 0;
                    const color = NAV_TEAM_COLORS[t] || "#666";
                    const isSelected = selectedItem?.type === "team" && selectedItem?.value === t;
                    return (
                      <div key={t}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("team", t)}
                        onClick={() => setSelectedItem(isSelected ? null : { type: "team", value: t })}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white cursor-grab active:cursor-grabbing select-none transition-all ${isSelected ? "ring-2 ring-offset-2 ring-teal-500 scale-110" : ""}`}
                        style={{ backgroundColor: color }}
                        data-testid={`nav-bank-${t.replace(/[^a-zA-Z]/g, "")}`}
                      >
                        {rank > 0 && <span className="bg-white/30 rounded-full px-1.5 mr-1 text-[10px]">#{rank}</span>}
                        {t}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Game Word Bank */}
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground mb-2 font-semibold">GAME BANK — drag or tap to select, then tap a game slot (auto-fills location)</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(activities).map(([activity, location]) => {
                    const isSelected = selectedItem?.type === "activity" && selectedItem?.value === activity;
                    return (
                      <div key={activity}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("activity", activity)}
                        onClick={() => setSelectedItem(isSelected ? null : { type: "activity", value: activity })}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 cursor-grab active:cursor-grabbing select-none transition-all ${isSelected ? "border-teal-500 bg-teal-50 ring-2 ring-teal-300 scale-105" : "border-gray-300 bg-white hover:border-gray-400"}`}
                        data-testid={`nav-activity-bank-${activity.replace(/[^a-zA-Z]/g, "")}`}
                      >
                        {activity}
                        <span className="text-[9px] text-muted-foreground ml-1">@ {location}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Game Rows */}
        {(data?.games || []).map((game, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="pt-3 pb-3">
              <div className="flex items-center gap-1 mb-2">
                <span className="text-[10px] font-bold text-muted-foreground">Game {i + 1}</span>
                {game.activity && (
                  <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded border ml-2">{game.activity} @ {game.location}</span>
                )}
                {isEditable && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-red-400 ml-auto" onClick={() => removeGame(i)} data-testid={`nav-remove-game-${i}`}>
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Team 1 drop zone */}
                <div
                  className={`min-w-[130px] h-10 rounded-lg border-2 flex items-center justify-center text-xs font-semibold transition-all ${
                    game.team1 ? "border-solid bg-white" : "border-dashed border-gray-300 bg-gray-50"
                  } ${isEditable ? "cursor-pointer hover:border-teal-400" : ""}`}
                  style={game.team1 ? { borderColor: NAV_TEAM_COLORS[game.team1], color: NAV_TEAM_COLORS[game.team1] } : {}}
                  onDragOver={isEditable ? (e) => e.preventDefault() : undefined}
                  onDrop={isEditable ? (e) => handleTeamDrop(i, "team1", e) : undefined}
                  onClick={() => isEditable && handleDropZoneTap(i, "team1")}
                  data-testid={`nav-game-${i}-team1`}
                >
                  {game.team1 || (isEditable ? "Drop / Tap Team" : "—")}
                </div>

                <span className="text-sm font-bold text-muted-foreground">vs.</span>

                {/* Team 2 drop zone */}
                <div
                  className={`min-w-[130px] h-10 rounded-lg border-2 flex items-center justify-center text-xs font-semibold transition-all ${
                    game.team2 ? "border-solid bg-white" : "border-dashed border-gray-300 bg-gray-50"
                  } ${isEditable ? "cursor-pointer hover:border-teal-400" : ""}`}
                  style={game.team2 ? { borderColor: NAV_TEAM_COLORS[game.team2], color: NAV_TEAM_COLORS[game.team2] } : {}}
                  onDragOver={isEditable ? (e) => e.preventDefault() : undefined}
                  onDrop={isEditable ? (e) => handleTeamDrop(i, "team2", e) : undefined}
                  onClick={() => isEditable && handleDropZoneTap(i, "team2")}
                  data-testid={`nav-game-${i}-team2`}
                >
                  {game.team2 || (isEditable ? "Drop / Tap Team" : "—")}
                </div>

                {/* Activity drop zone */}
                {isEditable && !game.activity && (
                  <div
                    className="min-w-[100px] h-10 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-[10px] text-muted-foreground cursor-pointer hover:border-teal-400 transition-all"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleActivityDrop(i, e)}
                    onClick={() => handleDropZoneTap(i, "activity")}
                    data-testid={`nav-game-${i}-activity-drop`}
                  >
                    Drop Game
                  </div>
                )}

                {/* Scores */}
                <div className="flex items-center gap-2 ml-auto">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[9px] text-muted-foreground font-medium truncate max-w-[80px]">{game.team1 || "Team 1"}</span>
                    {isEditable ? (
                      <input type="number"
                        className="w-16 h-9 text-sm text-center border-2 rounded-lg bg-white focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none font-bold"
                        value={game.score1 ?? ""}
                        onChange={e => updateGame(i, "score1", e.target.value)}
                        onBlur={saveGames}
                        placeholder="—"
                        data-testid={`nav-playoff-score-${i}-1`}
                      />
                    ) : <span className="w-16 h-9 text-sm text-center font-bold inline-flex items-center justify-center bg-muted/30 rounded-lg border">{game.score1 || "—"}</span>}
                  </div>
                  <span className="text-muted-foreground text-sm font-bold mt-3">-</span>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[9px] text-muted-foreground font-medium truncate max-w-[80px]">{game.team2 || "Team 2"}</span>
                    {isEditable ? (
                      <input type="number"
                        className="w-16 h-9 text-sm text-center border-2 rounded-lg bg-white focus:ring-2 focus:ring-teal-400 focus:border-teal-400 outline-none font-bold"
                        value={game.score2 ?? ""}
                        onChange={e => updateGame(i, "score2", e.target.value)}
                        onBlur={saveGames}
                        placeholder="—"
                        data-testid={`nav-playoff-score-${i}-2`}
                      />
                    ) : <span className="w-16 h-9 text-sm text-center font-bold inline-flex items-center justify-center bg-muted/30 rounded-lg border">{game.score2 || "—"}</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!data?.games || data.games.length === 0) && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            No playoff games yet. {isEditable && "Click '+ New Game' to start building the bracket."}
          </div>
        )}

        {saving && <p className="text-center text-[10px] text-muted-foreground">Saving...</p>}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════
// CHAMPIONSHIP — Same structure as Playoffs
// ═══════════════════════════════════════

export function NavChampionshipView({ headers, onBack, isEditable }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rankings, setRankings] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [champRes, rankingsRes] = await Promise.all([
        axios.get(`${API}/nav-intramurals/championship`, { headers }),
        axios.get(`${API}/nav-intramurals/rankings`, { headers }),
      ]);
      setData(champRes.data);
      setRankings(rankingsRes.data.rankings || []);
    } catch { toast.error("Failed to load championship"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async (games) => {
    setSaving(true);
    try {
      await axios.put(`${API}/nav-intramurals/championship`, { games }, { headers });
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const addGame = () => {
    const games = [...(data?.games || []), { team1: "", team2: "", score1: "", score2: "", activity: "", location: "" }];
    setData(prev => ({ ...prev, games }));
    save(games);
  };

  const updateGame = (index, field, value) => {
    const games = [...(data?.games || [])];
    games[index] = { ...games[index], [field]: value };
    if (field === "activity" && ACTIVITY_LOCATIONS[value]) {
      games[index].location = ACTIVITY_LOCATIONS[value];
    }
    setData(prev => ({ ...prev, games }));
  };

  const saveGames = () => save(data?.games || []);

  const removeGame = (index) => {
    const games = (data?.games || []).filter((_, i) => i !== index);
    setData(prev => ({ ...prev, games }));
    save(games);
  };

  const handleDropZoneTap = (gameIdx, field) => {
    if (!isEditable || !selectedItem) return;
    if (selectedItem.type === "team" && (field === "team1" || field === "team2")) {
      updateGame(gameIdx, field, selectedItem.value);
      setSelectedItem(null);
      saveGames();
    }
    if (selectedItem.type === "activity" && field === "activity") {
      const games = [...(data?.games || [])];
      games[gameIdx] = { ...games[gameIdx], activity: selectedItem.value, location: ACTIVITY_LOCATIONS[selectedItem.value] || "" };
      setData(prev => ({ ...prev, games }));
      save(games);
      setSelectedItem(null);
    }
  };

  const handleTeamDrop = (gameIdx, field, e) => {
    e.preventDefault();
    const team = e.dataTransfer.getData("team");
    if (team && (field === "team1" || field === "team2")) {
      updateGame(gameIdx, field, team);
      saveGames();
    }
  };

  const handleActivityDrop = (gameIdx, e) => {
    e.preventDefault();
    const activity = e.dataTransfer.getData("activity");
    if (activity) {
      const games = [...(data?.games || [])];
      games[gameIdx] = { ...games[gameIdx], activity, location: ACTIVITY_LOCATIONS[activity] || "" };
      setData(prev => ({ ...prev, games }));
      save(games);
    }
  };

  const teamBank = data?.team_bank || [];
  const activities = data?.activities || ACTIVITY_LOCATIONS;

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <NavHeader title="Navigator Championship" onBack={onBack}>
        {isEditable && (
          <Button size="sm" className="h-8 text-xs bg-violet-600 hover:bg-violet-700" onClick={addGame} data-testid="nav-add-champ-game-btn">
            <Plus className="w-3 h-3 mr-1" /> New Game
          </Button>
        )}
      </NavHeader>

      <div className="p-4 max-w-5xl mx-auto space-y-4">

        {selectedItem && (
          <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 border border-violet-300 rounded-lg animate-pulse">
            <span className="text-xs font-semibold text-violet-800">
              Selected: <span className="font-bold">{selectedItem.value}</span> — tap a {selectedItem.type === "team" ? "team slot" : "game slot"} to place it
            </span>
            <Button variant="ghost" size="sm" className="h-6 px-2 ml-auto" onClick={() => setSelectedItem(null)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}

        {isEditable && (
          <>
            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground mb-2 font-semibold">TEAM BANK — drag or tap to select, then tap a team slot</p>
                <div className="flex flex-wrap gap-2">
                  {teamBank.map(t => {
                    const rankData = rankings.find(r => r.team === t);
                    const rank = rankData?.rank || 0;
                    const color = NAV_TEAM_COLORS[t] || "#666";
                    const isSelected = selectedItem?.type === "team" && selectedItem?.value === t;
                    return (
                      <div key={t} draggable
                        onDragStart={(e) => e.dataTransfer.setData("team", t)}
                        onClick={() => setSelectedItem(isSelected ? null : { type: "team", value: t })}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white cursor-grab active:cursor-grabbing select-none transition-all ${isSelected ? "ring-2 ring-offset-2 ring-violet-500 scale-110" : ""}`}
                        style={{ backgroundColor: color }}
                        data-testid={`nav-champ-bank-${t.replace(/[^a-zA-Z]/g, "")}`}
                      >
                        {rank > 0 && <span className="bg-white/30 rounded-full px-1.5 mr-1 text-[10px]">#{rank}</span>}
                        {t}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground mb-2 font-semibold">GAME BANK — drag or tap to select, then tap a game slot (auto-fills location)</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(activities).map(([activity, location]) => {
                    const isSelected = selectedItem?.type === "activity" && selectedItem?.value === activity;
                    return (
                      <div key={activity} draggable
                        onDragStart={(e) => e.dataTransfer.setData("activity", activity)}
                        onClick={() => setSelectedItem(isSelected ? null : { type: "activity", value: activity })}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 cursor-grab active:cursor-grabbing select-none transition-all ${isSelected ? "border-violet-500 bg-violet-50 ring-2 ring-violet-300 scale-105" : "border-gray-300 bg-white hover:border-gray-400"}`}
                        data-testid={`nav-champ-activity-${activity.replace(/[^a-zA-Z]/g, "")}`}
                      >
                        {activity}
                        <span className="text-[9px] text-muted-foreground ml-1">@ {location}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {(data?.games || []).map((game, i) => (
          <Card key={i} className="overflow-hidden border-violet-200">
            <CardContent className="pt-3 pb-3">
              <div className="flex items-center gap-1 mb-2">
                <span className="text-[10px] font-bold text-violet-700">Game {i + 1}</span>
                {game.activity && (
                  <span className="text-[10px] px-2 py-0.5 bg-violet-50 rounded border border-violet-200 ml-2">{game.activity} @ {game.location}</span>
                )}
                {isEditable && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-red-400 ml-auto" onClick={() => removeGame(i)} data-testid={`nav-champ-remove-game-${i}`}>
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div
                  className={`min-w-[130px] h-10 rounded-lg border-2 flex items-center justify-center text-xs font-semibold transition-all ${game.team1 ? "border-solid bg-white" : "border-dashed border-gray-300 bg-gray-50"} ${isEditable ? "cursor-pointer hover:border-violet-400" : ""}`}
                  style={game.team1 ? { borderColor: NAV_TEAM_COLORS[game.team1], color: NAV_TEAM_COLORS[game.team1] } : {}}
                  onDragOver={isEditable ? (e) => e.preventDefault() : undefined}
                  onDrop={isEditable ? (e) => handleTeamDrop(i, "team1", e) : undefined}
                  onClick={() => isEditable && handleDropZoneTap(i, "team1")}
                  data-testid={`nav-champ-game-${i}-team1`}
                >
                  {game.team1 || (isEditable ? "Drop / Tap Team" : "—")}
                </div>

                <span className="text-sm font-bold text-muted-foreground">vs.</span>

                <div
                  className={`min-w-[130px] h-10 rounded-lg border-2 flex items-center justify-center text-xs font-semibold transition-all ${game.team2 ? "border-solid bg-white" : "border-dashed border-gray-300 bg-gray-50"} ${isEditable ? "cursor-pointer hover:border-violet-400" : ""}`}
                  style={game.team2 ? { borderColor: NAV_TEAM_COLORS[game.team2], color: NAV_TEAM_COLORS[game.team2] } : {}}
                  onDragOver={isEditable ? (e) => e.preventDefault() : undefined}
                  onDrop={isEditable ? (e) => handleTeamDrop(i, "team2", e) : undefined}
                  onClick={() => isEditable && handleDropZoneTap(i, "team2")}
                  data-testid={`nav-champ-game-${i}-team2`}
                >
                  {game.team2 || (isEditable ? "Drop / Tap Team" : "—")}
                </div>

                {isEditable && !game.activity && (
                  <div
                    className="min-w-[100px] h-10 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-[10px] text-muted-foreground cursor-pointer hover:border-violet-400 transition-all"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleActivityDrop(i, e)}
                    onClick={() => handleDropZoneTap(i, "activity")}
                    data-testid={`nav-champ-game-${i}-activity-drop`}
                  >
                    Drop Game
                  </div>
                )}

                <div className="flex items-center gap-2 ml-auto">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[9px] text-muted-foreground font-medium truncate max-w-[80px]">{game.team1 || "Team 1"}</span>
                    {isEditable ? (
                      <input type="number"
                        className="w-16 h-9 text-sm text-center border-2 rounded-lg bg-white focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none font-bold"
                        value={game.score1 ?? ""}
                        onChange={e => updateGame(i, "score1", e.target.value)}
                        onBlur={saveGames}
                        placeholder="—"
                        data-testid={`nav-champ-score-${i}-1`}
                      />
                    ) : <span className="w-16 h-9 text-sm text-center font-bold inline-flex items-center justify-center bg-muted/30 rounded-lg border">{game.score1 || "—"}</span>}
                  </div>
                  <span className="text-muted-foreground text-sm font-bold mt-3">-</span>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[9px] text-muted-foreground font-medium truncate max-w-[80px]">{game.team2 || "Team 2"}</span>
                    {isEditable ? (
                      <input type="number"
                        className="w-16 h-9 text-sm text-center border-2 rounded-lg bg-white focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none font-bold"
                        value={game.score2 ?? ""}
                        onChange={e => updateGame(i, "score2", e.target.value)}
                        onBlur={saveGames}
                        placeholder="—"
                        data-testid={`nav-champ-score-${i}-2`}
                      />
                    ) : <span className="w-16 h-9 text-sm text-center font-bold inline-flex items-center justify-center bg-muted/30 rounded-lg border">{game.score2 || "—"}</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!data?.games || data.games.length === 0) && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            No championship games yet. {isEditable && "Click '+ New Game' to start building the bracket."}
          </div>
        )}

        {saving && <p className="text-center text-[10px] text-muted-foreground">Saving...</p>}
      </div>
    </div>
  );
}
